import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayment, useRefundPayment } from '../hooks/usePayments';
import { formatMoney } from '@/shared/utils/money';
import { Button } from '@/shared/components/ui/button';
import { useAuth } from '@/modules/auth/hooks/useAuth';

interface Props {
  id: string;
}

export function PaymentDetailPage({ id }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: payment, isLoading, error } = usePayment(id);
  const refundMutation = useRefundPayment();
  const [showRefundDialog, setShowRefundDialog] = useState(false);

  const canRefund = user?.permissions.includes('payments.refund');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive">{t('common.error')}</p>
      </div>
    );
  }

  const isRefundable = payment.status === 'succeeded';

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('payments.detail.title')}</h1>
        {canRefund && isRefundable && (
          <Button variant="outline" onClick={() => setShowRefundDialog(true)}>
            {t('payments.refund.trigger')}
          </Button>
        )}
      </div>

      <div className="rounded-lg border divide-y">
        <DetailRow label={t('payments.id')} value={payment.id} mono />
        <DetailRow
          label={t('payments.status.label')}
          value={t(`payments.status.${payment.status}`, payment.status)}
        />
        <DetailRow
          label={t('payments.amount')}
          value={formatMoney(payment.amount, payment.currency)}
          mono
        />
        <DetailRow
          label={t('payments.fee')}
          value={formatMoney(payment.feeAmount, payment.currency)}
          mono
        />
        <DetailRow
          label={t('payments.net')}
          value={formatMoney(payment.netAmount, payment.currency)}
          mono
        />
        <DetailRow label={t('payments.provider')} value={payment.provider} />
        {payment.paymentMethodLast4 && (
          <DetailRow
            label={t('payments.card')}
            value={`${payment.paymentMethodType ?? 'card'} •••• ${payment.paymentMethodLast4}`}
          />
        )}
        {payment.failureCode && (
          <DetailRow label={t('payments.failureCode')} value={payment.failureCode} />
        )}
        {payment.processedAt && (
          <DetailRow
            label={t('payments.processedAt')}
            value={new Date(payment.processedAt).toLocaleString()}
          />
        )}
        <DetailRow
          label={t('payments.date')}
          value={new Date(payment.createdAt).toLocaleString()}
        />
      </div>

      {showRefundDialog && (
        <RefundDialog
          payment={payment}
          onClose={() => setShowRefundDialog(false)}
          onSubmit={({ amount, reason }) => {
            refundMutation.mutate(
              { id: payment.id, ...(amount !== undefined ? { amount } : {}), reason },
              { onSuccess: () => setShowRefundDialog(false) },
            );
          }}
          isPending={refundMutation.isPending}
        />
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono' : ''}>{value}</span>
    </div>
  );
}

interface RefundDialogProps {
  payment: { amount: number; currency: string };
  onClose: () => void;
  onSubmit: (data: { amount?: number; reason: string }) => void;
  isPending: boolean;
}

function RefundDialog({ payment, onClose, onSubmit, isPending }: RefundDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [amountInput, setAmountInput] = useState('');

  const maxMajor = payment.amount / 100;
  const parsedAmount = amountInput ? parseFloat(amountInput) : undefined;
  const amountMinor = parsedAmount !== undefined ? Math.round(parsedAmount * 100) : undefined;
  const isPartial = amountMinor !== undefined && amountMinor < payment.amount;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    onSubmit({
      ...(amountMinor !== undefined ? { amount: amountMinor } : {}),
      reason: reason.trim(),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl space-y-4">
        <h2 className="text-lg font-semibold">{t('payments.refund.title')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('payments.refund.amount')}</label>
            <p className="text-xs text-muted-foreground mb-1">
              {t('payments.refund.maxAmount', {
                max: formatMoney(payment.amount, payment.currency),
              })}
            </p>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxMajor}
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              placeholder={t('payments.refund.fullRefundPlaceholder')}
              className="w-full rounded border px-3 py-2 text-sm"
            />
            {isPartial && (
              <p className="text-xs text-muted-foreground mt-1">{t('payments.refund.partial')}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">{t('payments.refund.reason')}</label>
            <textarea
              required
              minLength={1}
              maxLength={500}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('payments.refund.reasonPlaceholder')}
              className="w-full rounded border px-3 py-2 text-sm resize-none"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !reason.trim()}>
              {isPending ? t('common.loading') : t('payments.refund.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
